import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { OutletProvider } from "@/contexts/OutletContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import TopLoader from "@/components/TopLoader";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import POS from "@/pages/POS";
import Tables from "@/pages/Tables";
import Menu from "@/pages/Menu";
import Customers from "@/pages/Customers";
import Inventory from "@/pages/Inventory";
import Reports from "@/pages/Reports";
import Orders from "@/pages/Orders";
import Users from "@/pages/Users";
import Outlets from "@/pages/Outlets";
import KOT from "@/pages/KOT";
import Suppliers from "@/pages/Suppliers";
import Purchases from "@/pages/Purchases";
import SupplierLedger from "@/pages/SupplierLedger";
import StockReport from "@/pages/StockReport";
import GSTReturns from "@/pages/GSTReturns";
import MenuReport from "@/pages/MenuReport";
import Settings from "@/pages/Settings";
import Shifts from "@/pages/Shifts";
import Expenses from "@/pages/Expenses";
import Feedback from "@/pages/Feedback";
import PublicOrder from "@/pages/PublicOrder";
import PublicFeedback from "@/pages/PublicFeedback";

function App() {
  useEffect(() => {
    document.title = "Order Raja";
    // Apply saved theme immediately
    import("@/lib/theme").then(({ applyTheme, getCurrentTheme }) =>
      applyTheme(getCurrentTheme()),
    );
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <TopLoader />
        <Toaster richColors closeButton position="top-right" />
        <Routes>
          {/* PUBLIC self-order routes — no auth, no AuthProvider needed for cleanliness but they're nested so wrap to share Toaster */}
          <Route path="/order/:outletId" element={<PublicOrder />} />
          <Route path="/order/:outletId/:tableId" element={<PublicOrder />} />
          <Route path="/feedback/:outletId" element={<PublicFeedback />} />

          {/* APP shell (auth-gated) */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <OutletProvider>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      element={
                        <ProtectedRoute>
                          <Layout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Dashboard />} />
                      <Route path="pos" element={<POS />} />
                      <Route path="tables" element={<Tables />} />
                      <Route path="kot" element={<KOT />} />
                      <Route path="orders" element={<Orders />} />
                      <Route path="shifts" element={<Shifts />} />
                      <Route path="expenses" element={<Expenses />} />
                      <Route
                        path="feedback"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Feedback />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="menu"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Menu />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="customers" element={<Customers />} />
                      <Route
                        path="inventory"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Inventory />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="stock-report"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <StockReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="suppliers"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Suppliers />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="purchases"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Purchases />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="supplier-ledger"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <SupplierLedger />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="reports"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <Reports />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="menu-report"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <MenuReport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="gst-returns"
                        element={
                          <ProtectedRoute roles={["manager"]}>
                            <GSTReturns />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="users"
                        element={
                          <ProtectedRoute roles={[]}>
                            <Users />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="outlets"
                        element={
                          <ProtectedRoute roles={[]}>
                            <Outlets />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <ProtectedRoute roles={[]}>
                            <Settings />
                          </ProtectedRoute>
                        }
                      />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </OutletProvider>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
